import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Upload, 
  Edit, 
  Trash2, 
  Plus,
  AlertCircle,
  Paperclip,
  X,
  Save,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { baseURL } from '@/lib/axios';

interface Document {
  id: string;
  name: string;
  number: string;
  fullName: string;
  attachments: string[];
  expiredOn: string;
  status: 'active' | 'expired' | 'pending' | 'draft';
  type: 'personal' | 'official';
  description?: string;
  uploadedOn: string;
  lastModified: string;
}

interface DocumentSectionProps {
  documents: Document[];
  onSave: (documents: Document[], files?: File[]) => void;
  title: string;
  documentType: 'personal' | 'official';
}

export const DocumentSection: React.FC<DocumentSectionProps> = ({ 
  documents, 
  onSave,
  title,
  documentType
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localDocuments, setLocalDocuments] = useState<Document[]>(documents);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: File | null }>({});
  const [isDragOver, setIsDragOver] = useState<{ [key: string]: boolean }>({});
  const [selectedDocument, setSelectedDocument] = useState<{ url: string; title: string; type: string; fileName?: string } | null>(null);
  const fileInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});

  React.useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);




  // Helper function to extract hash from document URL
  const getDocumentHash = (docUrl: string) => {
    if (!docUrl) return 'document.pdf';
    const parts = docUrl.split('/');
    return parts[parts.length - 1] || 'document.pdf';
  };

  // Function to get complete backend URL for documents
  const getCompleteBackendUrl = (docUrl: string) => {
    if (!docUrl) return '';
    const baseUrl = baseURL.replace('/api/v1', '');
    const hash = getDocumentHash(docUrl);
    return `${baseUrl}/uploads/document-attachments/${hash}`;
  };

  const handleSave = async () => {
    // Collect files for upload in the correct order - only for documents that have new files
    const filesToUpload: File[] = [];
    
    localDocuments.forEach(doc => {
      const file = uploadedFiles[doc.id];
      if (file) {
        filesToUpload.push(file);
      }
    });


    try {
      await onSave(localDocuments, filesToUpload);
      setIsEditing(false);
      setUploadedFiles({});
      toast.success(`${title} updated successfully`);
    } catch (error) {
      toast.error('Failed to save documents');
    }
  };

  const handleCancel = () => {
    setLocalDocuments(documents);
    setIsEditing(false);
    setUploadedFiles({});
    setIsDragOver({});
    toast.info('Edit cancelled');
  };

  const handleEdit = () => {
    setIsEditing(true);
    toast.info('Edit mode enabled');
  };

  const handleFileUpload = (documentId: string, file: File) => {
    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    
    if (file.size > maxSize) {
      toast.error('File size should be less than 10MB');
      return;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload PDF, JPG, JPEG, or PNG files only');
      return;
    }

    // Update uploaded files state for temporary display
    setUploadedFiles(prev => {
      const newFiles = { ...prev, [documentId]: file };
      return newFiles;
    });
    
    // Update the attachments field in local data to indicate this document has a new file
    // This helps the backend know which documents need file mapping
    setLocalDocuments(prev => {
      const updated = prev.map(doc => 
        doc.id === documentId 
          ? { ...doc, attachments: [] } // Empty array - backend will add the proper URL after processing
          : doc
      );
      return updated;
    });
    
    toast.success(`File selected successfully for document ${documentId}`);
    
    // Reset the file input after successful upload
    const fileInput = fileInputRefs.current[documentId];
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleFileChange = (documentId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(documentId, file);
    }
    
    // Don't reset the input value immediately - let the file upload complete first
    // e.target.value = '';
  };

  const handleDragOver = (documentId: string, e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(prev => ({ ...prev, [documentId]: true }));
  };

  const handleDragLeave = (documentId: string, e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(prev => ({ ...prev, [documentId]: false }));
  };

  const handleDrop = (documentId: string, e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(prev => ({ ...prev, [documentId]: false }));
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(documentId, file);
    }
  };

  const removeFile = (documentId: string) => {
    // Remove from uploaded files state
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[documentId];
      return newFiles;
    });
    
    // Also clear attachments from the document data
    setLocalDocuments(prev => {
      const updated = prev.map(doc => 
        doc.id === documentId 
          ? { ...doc, attachments: [] }
          : doc
      );
      return updated;
    });
    
    toast.info('File removed');
  };


  // Function to handle document viewing
  const handleViewDocument = (docUrl: string, title: string, type: string, fileName?: string) => {
    if (!docUrl) {
      toast.error('No document available for viewing');
      return;
    }
    setSelectedDocument({ url: docUrl, title, type, fileName });
  };

  // Function to handle document download
  const handleDownloadDocument = async (docUrl: string, fileName?: string) => {
    if (!docUrl) {
      toast.error('No document available for download');
      return;
    }
    
    try {
      const url = getCompleteBackendUrl(docUrl);
      
      // Get authorization token
      const token = localStorage.getItem('accessToken');
      
      // Fetch the file as a blob with authorization
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'document.pdf';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document. Please try again.');
    }
  };

  const addDocument = () => {
    const newDocument: Document = {
      id: Date.now().toString(),
      name: '',
      number: '',
      fullName: '',
      attachments: [],
      expiredOn: '',
      status: 'draft',
      type: documentType,
      description: '',
      uploadedOn: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    
    setLocalDocuments(prev => {
      const newDocs = [...prev, newDocument];
      return newDocs;
    });
  };

  const removeDocument = (id: string) => {
    // Remove the document from local state
    setLocalDocuments(localDocuments.filter(doc => doc.id !== id));
    
    // Clean up any uploaded file for this document
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[id];
      return newFiles;
    });
  };

  const updateDocument = (id: string, field: keyof Document, value: string) => {
    setLocalDocuments(localDocuments.map(doc => 
      doc.id === id ? { ...doc, [field]: value } : doc
    ));
  };

  const getStatusColor = (status: Document['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isExpired = (expiredOn: string) => {
    if (!expiredOn) return false;
    return new Date(expiredOn) < new Date();
  };

  return (
    <>
      <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-gray-900">
                  {title}
                </CardTitle>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {isEditing && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
                >
                  <Save className="h-3 w-3" />
                  Save
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={isEditing ? handleCancel : handleEdit}
                className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
              >
                {isEditing ? (
                  <>
                    <X className="h-3 w-3" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Edit className="h-3 w-3" />
                    Edit
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="space-y-3">
            {localDocuments && localDocuments.map((doc, index) => (
              <div key={doc.id} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                {isEditing ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-sm text-blue-600">{title} {index + 1}</h5>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeDocument(doc.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">Document Name *</Label>
                        <Input
                        value={doc.name}
                        onChange={(e) => updateDocument(doc.id, 'name', e.target.value)}
                          placeholder="e.g., Passport, Driver's License"
                          className="h-8 text-xs"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">Document Number *</Label>
                        <Input
                        value={doc.number}
                        onChange={(e) => updateDocument(doc.id, 'number', e.target.value)}
                          placeholder="e.g., A1234567"
                          className="h-8 text-xs"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">Full Name on Document</Label>
                        <Input
                        value={doc.fullName}
                        onChange={(e) => updateDocument(doc.id, 'fullName', e.target.value)}
                          placeholder="Full name as it appears on document"
                          className="h-8 text-xs"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">Expiry Date</Label>
                        <Input
                          type="date"
                        value={doc.expiredOn}
                        onChange={(e) => updateDocument(doc.id, 'expiredOn', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">Status</Label>
                        <select
                        value={doc.status}
                        onChange={(e) => updateDocument(doc.id, 'status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-8 text-xs"
                        >
                          <option value="draft">Draft</option>
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                      
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs font-medium text-gray-700">Description</Label>
                        <Textarea
                        value={doc.description || ''}
                        onChange={(e) => updateDocument(doc.id, 'description', e.target.value)}
                          placeholder="Additional notes about this document"
                          rows={2}
                          className="text-xs"
                        />
                      </div>
                      
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs font-medium text-gray-700">Attachment</Label>
                        {uploadedFiles[doc.id] || doc.attachments.length > 0 ? (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center gap-2">
                              <Paperclip className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-700">
                                {uploadedFiles[doc.id]?.name || 
                                 (doc.attachments[0] ? getDocumentHash(doc.attachments[0]) : 'No file selected')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeFile(doc.id)}
                                className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div 
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                    isDragOver[doc.id] ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={(e) => handleDragOver(doc.id, e)}
                  onDragLeave={(e) => handleDragLeave(doc.id, e)}
                  onDrop={(e) => handleDrop(doc.id, e)}
                  onClick={() => {
                    const fileInput = fileInputRefs.current[doc.id];
                    if (fileInput) {
                      fileInput.click();
                    } else {
                      // Try to find the input by ID as fallback
                      const inputById = document.getElementById(`file-upload-${doc.id}`) as HTMLInputElement;
                      if (inputById) {
                        inputById.click();
                      }
                    }
                  }}
                          >
                            <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                            <div className="text-sm">
                              <span className="text-blue-600 font-medium">Click to upload</span>
                              <span className="text-gray-500"> or drag and drop</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              PDF, JPG, JPEG, PNG up to 10MB
                            </p>
                            <input
                    key={`file-input-${doc.id}`}
                    ref={(el) => {
                      fileInputRefs.current[doc.id] = el;
                    }}
                    id={`file-upload-${doc.id}`}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(doc.id, e)}
                              className="hidden"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                            <FileText className="h-3 w-3 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-sm text-gray-900">
                              {doc.name || 'Document Name'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {doc.number || 'Document Number'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${getStatusColor(doc.status)}`}
                          >
                            {doc.status || 'Draft'}
                          </Badge>
                          {isExpired(doc.expiredOn) && (
                            <Badge variant="destructive" className="flex items-center gap-1 text-xs">
                              <AlertCircle className="h-3 w-3" />
                              Expired
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-200">
                        {doc.fullName && (
                          <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3 text-blue-600" />
                            <span className="text-xs text-gray-600">Name: {doc.fullName}</span>
                          </div>
                        )}
                        {doc.expiredOn && (
                          <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3 text-blue-600" />
                            <span className={`text-xs ${isExpired(doc.expiredOn) ? 'text-red-600' : 'text-gray-600'}`}>
                              Expiry: {new Date(doc.expiredOn).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {doc.description && (
                          <div className="flex items-center gap-1 md:col-span-2">
                            <FileText className="h-3 w-3 text-blue-600" />
                            <span className="text-xs text-gray-600">Description: {doc.description}</span>
                          </div>
                        )}
                        {doc.attachments.length > 0 && (
                          <div className="flex items-center gap-2 md:col-span-2">
                            <Paperclip className="h-3 w-3 text-blue-600" />
                            <span className="text-xs text-gray-600">Attachment: {getDocumentHash(doc.attachments[0])}</span>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDocument(
                                  doc.attachments[0],
                                  `${title} - ${doc.name}`,
                                  documentType,
                                  getDocumentHash(doc.attachments[0])
                                )}
                                className="h-4 w-4 p-0 text-gray-400 hover:text-green-600"
                                title="View Document"
                              >
                                <FileText className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={addDocument}
                className="gap-1 w-full text-xs"
              >
                <Plus className="h-3 w-3" />
                Add {title}
              </Button>
            )}
            
            {!isEditing && localDocuments.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No {title}</h3>
                <p className="text-sm text-gray-500 mb-4">No {title.toLowerCase()} have been recorded for this employee.</p>
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs text-gray-400">Click "Edit" to add new {title.toLowerCase()}</p>
                  <div className="w-32 h-1 bg-gray-200 rounded-full"></div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{selectedDocument.title}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDocument(null)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Document Type: {selectedDocument.type}</span>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <a 
                href={getCompleteBackendUrl(selectedDocument.url)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-all text-sm font-medium cursor-pointer block bg-gray-50 p-2 rounded border"
              >
                {selectedDocument.fileName || 'document.pdf'}
              </a>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const url = getCompleteBackendUrl(selectedDocument.url);
                  window.open(url, '_blank');
                  toast.info("Document opened in new tab. Right-click and 'Save as' to download.");
                }}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
              >
                <FileText className="h-4 w-4 mr-2" />
                Open in New Tab
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleDownloadDocument(selectedDocument.url, selectedDocument.fileName)}
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Export individual components for backward compatibility
export const PersonalDocSection: React.FC<{ documents: Document[]; onSave: (documents: Document[], files?: File[]) => void }> = ({ documents, onSave }) => (
  <DocumentSection 
    documents={documents} 
    onSave={onSave}
    title="Personal Documents"
    documentType="personal"
  />
);

export const OfficialDocSection: React.FC<{ documents: Document[]; onSave: (documents: Document[], files?: File[]) => void }> = ({ documents, onSave }) => (
  <DocumentSection 
    documents={documents} 
    onSave={onSave}
    title="Official Documents"
    documentType="official"
  />
);
